import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import Button from '../components/Button';
import QuizResponsesDisplay from '../components/QuizResponsesDisplay';
import BreedRecommendations from '../components/BreedRecommendations';
import { hasCompletedQuiz, loadQuizResponses } from '../utils/quizStorage';

function Profile() {
  const hasQuizData = hasCompletedQuiz();
  const quizResponses = loadQuizResponses();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faUser} className="h-8 w-8 text-primary-500 mr-3" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
              Your Profile
            </h1>
          </div>
          <p className="text-lg text-neutral-600 dark:text-neutral-300">
            Manage your pet preferences and view your matches
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">
                Profile Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Location
                  </label>
                  <input
                    id="location"
                    type="text"
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="City, State"
                  />
                </div>
                
                <Button variant="primary" className="w-full mt-6">
                  Save Profile
                </Button>
              </div>
            </div>
          </div>

          {/* Quiz Results and Matches */}
          <div className="lg:col-span-2 space-y-8">
            {hasQuizData ? (
              <>
                {/* Quiz Responses */}
                <div className="space-y-4">
                  <QuizResponsesDisplay />
                  
                  {/* Retake Quiz Option */}
                  <div className="bg-neutral-50 dark:bg-neutral-700 rounded-lg p-4 text-center">
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
                      Want to update your preferences or get new matches?
                    </p>
                    <Link to="/quiz">
                      <Button variant="outline" size="small">
                        Retake Compatibility Quiz
                      </Button>
                    </Link>
                  </div>
                </div>
                
                {/* Breed Recommendations */}
                <BreedRecommendations userPreferences={quizResponses} limit={5} />
              </>
            ) : (
              /* No Quiz Data - Encourage Taking Quiz */
              <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
                <FontAwesomeIcon icon={faClipboardList} className="h-16 w-16 text-primary-500 mb-6" />
                <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-4">
                  Start Your Pet Matching Journey
                </h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-6">
                  Take our compatibility quiz to discover pets that match your lifestyle and preferences.
                </p>
                
                <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-3">
                    The quiz helps us understand:
                  </h3>
                  <ul className="text-left text-primary-700 dark:text-primary-200 space-y-2">
                    <li>• Your experience level with pets</li>
                    <li>• Your living situation and space</li>
                    <li>• How much time you can dedicate to a pet</li>
                    <li>• Your preferred pet size and energy level</li>
                  </ul>
                </div>
                
                <Link to="/quiz">
                  <Button variant="primary" size="large">
                    <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                    Take the Compatibility Quiz
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;